import React, { useMemo, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useStore } from '../../store';
import { Plus } from 'lucide-react';
import TaskCard from '../Tasks/TaskCard';
import { STATUS } from '../../utils';
import CreateTaskModal from '../Tasks/CreateTaskModal';
import { usePermissions } from '../../hooks/usePermissions';

const COLUMNS = [
  { id: 'backlog', label: 'Бэклог' },
  { id: 'todo', label: 'К выполнению' },
  { id: 'in_progress', label: 'В работе' },
  { id: 'review', label: 'На ревью' },
  { id: 'done', label: 'Готово' },
];

export default function KanbanBoard() {
  const { tasks, updateTask, createTask, activeProject, openTask } = useStore();
  const [showCreate, setShowCreate] = useState(null);
  const { canCreateTask } = usePermissions();
  const canCreate = canCreateTask(activeProject);
  const columns = useMemo(() => {
    const map = {};
    COLUMNS.forEach(c => { map[c.id] = []; });
    tasks.filter(t => !t.parent).forEach(t => {
      if (map[t.status]) map[t.status].push(t);
      else map['backlog'].push(t);
    });
    return map;
  }, [tasks]);

  const onDragEnd = ({ source, destination, draggableId }) => {
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;
    updateTask(draggableId, { status: destination.droppableId, order: destination.index });
  };

  const addTask = async (status) => {
    const title = prompt('Название задачи:');
    if (!title?.trim()) return;
    await createTask({ title: title.trim(), project: activeProject._id, status });
  };

  console.log('Rendering KanbanBoard with tasks:', canCreate);

  return (
    <DragDropContext onDragEnd={canCreate ? onDragEnd : () => { }}>
      <div className="h-full overflow-x-auto overflow-y-hidden">
        <div className="flex gap-3 p-3 sm:p-4 h-full" style={{ minWidth: `${COLUMNS.length * 300}px` }}>
          {COLUMNS.map(col => {
            const s = STATUS[col.id];
            const colTasks = columns[col.id] || [];
            return (
              <div key={col.id} className="w-72 flex flex-col flex-shrink-0">
                {/* Column header */}
                <div className="flex items-center gap-2 mb-2 px-1">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: s?.color }} />
                  <span className="text-sm font-medium">{col.label}</span>
                  <span className="ml-auto text-xs text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-full w-5 h-5 flex items-center justify-center">
                    {colTasks.length}
                  </span>
                </div>

                {/* Cards */}
                <Droppable
                  droppableId={col.id}
                  isDragDisabled={!canCreate}
                  isDropDisabled={!canCreate}
                  disabled={!canCreate}
                  direction="vertical"
                  ignoreContainerClipping={true}
                >
                  {(provided, snapshot) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}
                      className={`flex-1 space-y-2 rounded-xl p-2 transition-colors overflow-y-auto ${snapshot.isDraggingOver ? 'bg-brand-50 dark:bg-brand-900/20' : 'bg-gray-50 dark:bg-gray-800/50'}`}
                      style={{ minHeight: 80 }}>
                      {colTasks.map((task, index) => (
                        <Draggable key={task._id} draggableId={task._id} index={index}>
                          {(prov, snap) => (
                            <div ref={prov.innerRef} {...prov.draggableProps} {...prov.dragHandleProps}
                              className={`${snap.isDragging ? 'rotate-1 shadow-xl' : ''} transition-transform`}>
                              <TaskCard task={task} onClick={() => openTask(task._id)} />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>

                {/* Add task */}
                {canCreate && (
                  <button onClick={() => setShowCreate(col.id)}
                    className="mt-2 flex items-center gap-1.5 px-2 py-2 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors w-full">
                    <Plus className="w-3.5 h-3.5" /> Добавить задачу
                  </button>
                )}
                {!canCreate && (
                  <button disabled title="Требуется роль участника или выше" onClick={() => setShowCreate(col.id)}
                    className="mt-2 opacity-50 flex items-center gap-1.5 px-2 py-2 text-xs text-gray-400 rounded-lg transition-colors w-full cursor-not-allowed">
                    <Plus className="w-3.5 h-3.5" /> Добавить задачу
                  </button>
                )}
              </div>
            );
          })}
          {showCreate && (
            <CreateTaskModal
              onClose={() => setShowCreate(null)}
              defaultStatus={showCreate}
            />
          )}
        </div>
      </div>
    </DragDropContext>
  );
}
